import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaPlus } from 'react-icons/fa';
import { Page, PageHeader, Card, SearchInput, Modal, Field, EmptyRow, Alert, Loading, formatDate } from '../../ui';
import { api } from '../../ui/api';

const CategoryScreen = () => {
  const [categories, setCategories] = useState(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // {} for new, a category to edit
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setCategories(await api('/category/all_categories'));
    } catch (e) {
      setLoadError(e.message);
      setCategories([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (category) => {
    setEditing(category);
    setName(category.name || '');
    setError('');
  };
  const close = () => setEditing(null);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing.id) {
        await api(`/category/edit_category/${editing.id}`, { method: 'PUT', body: { name } });
      } else {
        await api('/category/add_category', { method: 'POST', body: { name } });
      }
      close();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const rows = (categories || []).filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const lastModified = (c) => (c.modification_dates && c.modification_dates.length
    ? formatDate(c.modification_dates[c.modification_dates.length - 1]) : '—');

  return (
    <Page>
      <PageHeader
        title="Categories"
        subtitle="Groups that services are listed under in the customer app."
        actions={<button className="btn btn-primary" onClick={() => open({})}><FaPlus /> Add category</button>}
      />
      <Alert>{loadError}</Alert>
      <Card flush>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search categories" />
          {categories && <span className="count">{rows.length} of {categories.length}</span>}
        </div>
        {!categories ? <Loading /> : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created</th>
                  <th>Last modified</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((category) => (
                  <tr key={category.id}>
                    <td data-label="Name" className="strong">{category.name}</td>
                    <td data-label="Created" className="nowrap">{formatDate(category.date_created)}</td>
                    <td data-label="Last modified" className="nowrap">{lastModified(category)}</td>
                    <td className="actions-cell">
                      <button className="btn btn-secondary btn-sm" onClick={() => open(category)}><FaEdit /> Rename</button>
                    </td>
                  </tr>
                )) : <EmptyRow cols={4}>{query ? 'No categories match your search.' : 'No categories yet.'}</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={!!editing}
        title={editing && editing.id ? 'Rename category' : 'Add category'}
        onClose={close}
        onSubmit={submit}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        )}
      >
        <div className="form">
          <Alert>{error}</Alert>
          <Field label="Category name">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus placeholder="e.g. Kitchen & Bathroom" />
          </Field>
        </div>
      </Modal>
    </Page>
  );
};

export default CategoryScreen;
